import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ContextService, initialStatus, LoadingStatus, Typology } from '@bcef/ia-shared';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, map, Observable, switchMap, tap } from 'rxjs';

import { State } from '../enums/states.enum';
import { SubscriptionTypes } from '../enums/subscription-types.enum';
import { ConfigResponse } from '../models/config-response.model';
import { SponsorResponse } from '../models/sponsor-response.model';
import { SponsorshipResponse } from '../models/sponsorship-response.model';
import { SubscriptionPayload } from '../models/subscription-payload.model';
import { SponsorshipService } from '../services/sponsorship.service';

export interface SponsorshipState {
  code: string | null;
  token: string | null;
  configResponse: ConfigResponse | null;
  sponsorshipResponse: SponsorshipResponse | null;
  sponsorResponse: SponsorResponse | null;
  sponsorshipLoadingStatus: LoadingStatus;
  subscriptionPayload: SubscriptionPayload | null;
  subscriptionLoadingStatus: LoadingStatus;
}

export const sponsorshipInitialState: SponsorshipState = {
  code: null,
  token: null,
  configResponse: null,
  sponsorshipResponse: null,
  sponsorResponse: null,
  sponsorshipLoadingStatus: initialStatus,
  subscriptionPayload: null,
  subscriptionLoadingStatus: initialStatus,
};

export const sponsorshipStore = signalStore(
  { providedIn: 'root' },

  withState<SponsorshipState>(sponsorshipInitialState),

  withComputed((store) => ({
    sponsorshipResponse: computed(() => store.sponsorshipResponse()),
    sponsorResponse: computed(() => store.sponsorResponse()),
    sponsorshipLoadingStatus: computed(() => store.sponsorshipLoadingStatus()),
    subscriptionTypes: computed(() => store.subscriptionPayload()?.subscriptionType),
    countryCode: computed(() => store.subscriptionPayload()?.countryCode),
    localNumber: computed(() => store.subscriptionPayload()?.localNumber),
    accountType: computed(() => store.subscriptionPayload()?.accountType),
  })),

  withMethods(
    (
      store,
      sponsorshipService = inject(SponsorshipService),
      contextService = inject(ContextService),
    ) => {
      const router = inject(Router);

      const getConfig = rxMethod<void>((config$) =>
        config$.pipe(
          switchMap(() =>
            sponsorshipService.getConfig().pipe(
              tap((resp: ConfigResponse) => {
                patchState(store, {
                  configResponse: resp,
                });
              }),
              catchError(() => EMPTY),
            ),
          ),
        ),
      );

      const fetchSponsorship = rxMethod<string>((sponsorship$) =>
        sponsorship$.pipe(
          tap(() => {
            patchState(store, {
              sponsorshipResponse: null,
              sponsorshipLoadingStatus: { ...initialStatus, isLoading: true },
            });
          }),
          switchMap((code) =>
            sponsorshipService.fetchSponsorship(code).pipe(
              tap((resp: SponsorshipResponse) => {
                patchState(store, {
                  code: code,
                  sponsorshipResponse: resp,
                  sponsorshipLoadingStatus: { ...initialStatus, isLoaded: true },
                });
                if (!Object.values<string>(State).includes(resp.currentState)) {
                  router.navigate(['error'], { skipLocationChange: true });
                } else if (
                  resp.currentState === State.EXPIRED ||
                  resp.currentState === State.CLOSED
                ) {
                  router.navigate(['expired-page'], { skipLocationChange: true });
                }
              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, {
                  sponsorshipLoadingStatus: {
                    ...initialStatus,
                    error: error,
                  },
                });
                router.navigate(['error'], { skipLocationChange: true });
                return EMPTY;
              }),
            ),
          ),
        ),
      );

      const fetchSponsor = rxMethod<string>((sponsor$) =>
        sponsor$.pipe(
          tap((token) => {
            patchState(store, {
              sponsorResponse: null,
              token: token,
            });
          }),
          switchMap((token) =>
            sponsorshipService.fetchSponsor(token).pipe(
              tap((resp: SponsorResponse) => {
                patchState(store, {
                  sponsorResponse: resp,
                });
              }),
              catchError(() => EMPTY),
            ),
          ),
        ),
      );

      const navigateToAcknowledgment = (): void => {
        const typology = contextService.typology;

        const indicatorMap = {
          0: 'error',
          1: 'digital-acknowledgment',
          2: 'digital-acknowledgment-qrcode',
        };

        const config = store.configResponse();
        const indicators = config?.acknowledgmentPageIndicator;

        const indicatorValue =
          (indicators as Record<Typology, number> | undefined)?.[typology] ?? 0;
        const digitalRoute = indicatorMap[indicatorValue as keyof typeof indicatorMap] ?? 'error';

        const route =
          store.subscriptionTypes() === SubscriptionTypes.DIGITAL
            ? digitalRoute
            : 'on-site-acknowledgment';
        router.navigate([route], { skipLocationChange: true });
      };

      const fetchSponsorshipAndSponsor = (code: string, token: string): void => {
        getConfig();
        fetchSponsorship(code);
        fetchSponsor(token);
      };

      const postSubscription = (payload: SubscriptionPayload): Observable<void> => {
        patchState(store, {
          subscriptionPayload: payload,
          subscriptionLoadingStatus: { ...initialStatus, isLoading: true },
        });

        return sponsorshipService.postSubscription(payload).pipe(
          tap(() => {
            patchState(store, {
              subscriptionLoadingStatus: { ...initialStatus, isLoaded: true },
            });
          }),
          tap(() => {
            queueMicrotask(() => navigateToAcknowledgment());
          }),
          map(() => void 0),
          catchError((error: HttpErrorResponse) => {
            patchState(store, {
              subscriptionLoadingStatus: {
                ...initialStatus,
                error: error,
              },
            });
            queueMicrotask(() => router.navigate(['error'], { skipLocationChange: true }));
            return EMPTY;
          }),
        );
      };

      return { fetchSponsorship, fetchSponsor, fetchSponsorshipAndSponsor, postSubscription };
    },
  ),
);

export type ISponsorshipStore = InstanceType<typeof sponsorshipStore>;
